import { AfterViewInit, Component, ElementRef, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, QueryList, Renderer2, ViewChild, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { forkJoin, NEVER, of } from 'rxjs';
import { catchError, delay, take } from 'rxjs/operators';
import { fadeIn, fadeOut } from 'src/app/animations';
import { UserPlatformType } from 'src/app/models/enum/user-platform-type.enum';
import { AuthService } from 'src/app/services/auth.service';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { GlobalUtilsService } from 'src/app/services/ui/global-utils.service';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { environment } from 'src/environments/environment';
import { NavigationActionButton } from '../navigation-bar/models/navigation-action-button.model';
import { LoginDevice, LoginType } from './models/login-device.enum';
import { LoginRedirectPage } from './models/login-redirect-page.enum';

@Component({
	selector: 'login-wizard',
	templateUrl: './login-wizard.component.html',
	styleUrls: ['./login-wizard.component.scss', '../stylesheets/job-actions.scss', '../stylesheets/job-main.scss', '../stylesheets/job-inputs.scss', '../stylesheets/forms-container.scss'],
	animations: [fadeIn(200), fadeOut(200)]
})
export class LoginWizardComponent implements OnInit, AfterViewInit, OnDestroy {
    readonly LoginType = LoginType;
	public sections = [
		true,
		false,
		false,
		false
	];

	public isDataLoading = false;

	public useMobile = false;
	public page: LoginRedirectPage = LoginRedirectPage.MyWork;
	public preferredPlatformTypeId: UserPlatformType = null;
	public isAutoredirect = false;
	public preferredAccountId = null;

	public isUserSmartSyncOnly = false;

	public emailFormGroup: FormGroup;
	public userTypeFormGroup: FormGroup;

	public userPlatformTypes: UserPlatformType[] = null;
	public isEmailCached: boolean;

	public readonly LoginDevice = LoginDevice;
	public readonly UserPlatformType = UserPlatformType;

	public get currentlyVisibleSection(): number {
		for (let index = this.sections.length - 1; index >= 0; --index) {
			if (this.sections[index]) {
				return index;
			}
		}
	}

	constructor(
		private userAuthService: UserAuthService,
		private authService: AuthService,
		private activatedRoute: ActivatedRoute,
		private jwt: JwtHelperService,
		private router: Router
	) { }

	ngOnInit(): void {
		GlobalSettingsService.priorityNavigationBarActions = new Map<string, NavigationActionButton>();
		this.isEmailCached = !!localStorage.getItem('cloud-email');

		this.emailFormGroup = new FormGroup({
			email: new FormControl(this.isEmailCached ? localStorage.getItem('cloud-email') : '', [Validators.required, Validators.email, Validators.minLength(3), Validators.pattern(/\S+/)]),
			loginType: new FormControl(LoginType[localStorage.getItem('cloud-login-type')] || LoginType.Id, Validators.required)
		});


		this.userTypeFormGroup = new FormGroup({
			hasCustomer: new FormControl(false),
			hasVendor: new FormControl(false),
			selectedUserType: new FormControl(null)
		});

		this.emailFormGroup.get('email').valueChanges.subscribe(
			value => {
				this.checkCrtValue();
			}
		);

		const customEmail = this.activatedRoute.snapshot.queryParamMap.get('email');
		this.useMobile = !!this.activatedRoute.snapshot.queryParamMap.get('useMobile');
		this.page = parseInt(this.activatedRoute.snapshot.queryParamMap.get('page') || '1', 10);

		this.isAutoredirect = !!this.activatedRoute.snapshot.queryParamMap.get('isAutoredirect');
		this.preferredAccountId = this.activatedRoute.snapshot.queryParamMap.get('accountId');

		if (!!this.activatedRoute.snapshot.queryParamMap.get('platformTypeId')) {
			const paramPlatformType = parseInt(this.activatedRoute.snapshot.queryParamMap.get('platformTypeId'), 10);

			if (!isNaN(paramPlatformType) && [UserPlatformType.Customer, UserPlatformType.Vendor].includes(paramPlatformType)) {
				this.preferredPlatformTypeId = paramPlatformType;
				localStorage.setItem('platform_type_id', this.preferredPlatformTypeId.toString());
			} else {
				this.preferredPlatformTypeId = null;
			}
		}

		const localStoragePlatformTypeId = parseInt(localStorage.getItem('platform_type_id'), 10);
		this.userTypeFormGroup.get('selectedUserType')
			.setValue(isNaN(localStoragePlatformTypeId) ? null : localStoragePlatformTypeId);

		if (customEmail) {
			this.emailFormGroup.get('email').setValue(customEmail);
			this.checkEmail();
		} else if (!!this.isAutoredirect) {
			const token = this.jwt.decodeToken();
			if (!!token && !!token?.email) {
				this.emailFormGroup.get('email').setValue(token[AuthService.EmailClaim]);
				this.checkEmail();
			}
		}
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			GlobalSettingsService.isGlobalLoaderLoading = false;
			GlobalSettingsService.isSecondaryLoaderLoading = false;
		}, 0);
	}

	ngOnDestroy(): void {
		GlobalSettingsService.priorityNavigationBarActions = null;
	}

	public selectNextSection(): void {
		const currentSection = this.currentlyVisibleSection;
		this.sections[currentSection + 1] = true;
	}

	public selectPreviousSection(unhideIfHidden: boolean = false): void {
		const currentSection = this.currentlyVisibleSection;

		if (unhideIfHidden) {
			this.sections[currentSection - 1] = true;
			this.sections[currentSection] = false;
		} else {
			this.sections[currentSection] = false;
		}
	}

	public jumpToSection(sectionNumber: number): void {
		this.sections[sectionNumber] = true;

		const currentSection = this.currentlyVisibleSection;

		if (sectionNumber < currentSection) {
			for (let index = sectionNumber + 1; index < this.sections.length; ++index) {
				this.sections[index] = false;
			}
		}
	}
	keyPressed($event: any){
		if ($event.keyCode === 13 /*Enter */){
			this.checkEmail();
		}
	}

	checkCrtValue(){
		const model = this.emailFormGroup.getRawValue();
		if (model.email.indexOf(' ') >= 0){
			this.emailFormGroup.patchValue({email: model.email.replaceAll(' ', '')});
		}
	}

	public checkEmail(): void {
		if (this.emailFormGroup.invalid) {
			return;
		}

		this.isDataLoading = true;

		const model = this.emailFormGroup.getRawValue();

		if (model.loginType == LoginType.Location) {
			localStorage.setItem('cloud-email', model.email.trim());
			localStorage.setItem('cloud-login-type', LoginType[model.loginType]);
			this.router.navigate(['/binder']);
		} else {
			forkJoin([
				this.userAuthService.getPlatformTypes(model.email.trim()),
				this.authService.isVendorUserSmartSyncOnly(model.email.trim())
			])
				.pipe(catchError(() => {
					this.userPlatformTypes = [];
					this.isDataLoading = false;
					return NEVER;
				}))
				.subscribe(([types, isUserSmartSyncOnly]) => {
					this.isUserSmartSyncOnly = isUserSmartSyncOnly;
					this.userPlatformTypes = types || [];

					if (!types?.length) {
						this.isDataLoading = false;
						return;
					}
					localStorage.setItem('cloud-email', model.email.trim());
					localStorage.setItem('cloud-login-type', LoginType[model.loginType]);

					const token = this.jwt.decodeToken();
					const tokenPlatformTypeId = !!token ? parseInt(token[AuthService.PlatformTypeIdClaim], 10) : null;

					const localStoragePlatformTypeId = parseInt(localStorage.getItem('platform_type_id') || null, 10);

					if (this.preferredPlatformTypeId && types.includes(this.preferredPlatformTypeId)) {
						this.selectUserType(this.preferredPlatformTypeId);
						this.jumpToSection(2);
					} else if (this.isAutoredirect &&
						!!token &&
						!!tokenPlatformTypeId &&
						!isNaN(tokenPlatformTypeId) &&
						!!this.userPlatformTypes.find(item => item === tokenPlatformTypeId)
					) {
						this.selectUserType(tokenPlatformTypeId);
						this.jumpToSection(2);
					} else if (this.isAutoredirect &&
						!!token &&
						(!tokenPlatformTypeId || isNaN(tokenPlatformTypeId)) &&
						!!localStoragePlatformTypeId &&
						!isNaN(localStoragePlatformTypeId) &&
						!!this.userPlatformTypes.find(item => item === localStoragePlatformTypeId)
					) {
						this.selectUserType(localStoragePlatformTypeId);
						this.jumpToSection(2);
					} else {
						const hasCustomer = types && types.includes(UserPlatformType.Customer);
						const hasVendor = types && types.includes(UserPlatformType.Vendor);

						this.userTypeFormGroup.get('hasVendor').setValue(hasVendor);
						this.userTypeFormGroup.get('hasCustomer').setValue(hasCustomer);

						if (hasCustomer && hasVendor) {
							this.selectNextSection();
						} else {
							this.selectUserType(types[0]);
							this.jumpToSection(2);
						}
					}
					this.isDataLoading = false;
				});
		}
	}

	public selectUserType(userType: UserPlatformType): void {
		this.userTypeFormGroup.get('selectedUserType').setValue(userType);
		localStorage.setItem('platform_type_id', `${userType}`);

		// Redirect without further steps
		if (this.useMobile) {
			this.selectDevice(LoginDevice.Mobile);
		} else {
			if (userType === UserPlatformType.Customer) {
				this.selectDevice(LoginDevice.Desktop);
			} else if (userType === UserPlatformType.Vendor) {
				GlobalUtilsService.isMobileDevice ? this.selectDevice(LoginDevice.Mobile) : this.selectDevice(LoginDevice.Desktop);
			}
		}
	}

	public selectDevice(device: LoginDevice): void {
		const userType: UserPlatformType = this.userTypeFormGroup.get('selectedUserType').value;

		if (device === LoginDevice.Desktop) {
			if (userType === UserPlatformType.Vendor) {
				if (this.isUserSmartSyncOnly) {
					this.redirectToSmartSync(userType);
				} else {
					location.href = environment.platforms.vendor + '/login';
				}
			}
			if (userType === UserPlatformType.Customer) { location.href = environment.platforms.customer + '/login'; }

		}

		if (device === LoginDevice.Mobile) {
			this.redirectToSmartSync(userType);
		}
	}

	public redirectToSmartSync(userType: UserPlatformType): void {
		this.authService.isUserAuthenticated(this.emailFormGroup.get('email').value)
		.pipe(
			catchError(() => of(false))
		)
		.subscribe(isAuthenticated => {
			const userDetails = this.jwt.decodeToken();

			if (!!userDetails && isAuthenticated && userDetails[AuthService.EmailClaim] === this.emailFormGroup.get('email').value) {
				if (userDetails[AuthService.PlatformTypeIdClaim] === userType.toString()) {
					location.href = `${location.origin}/accounts?page=` + this.page + (this.preferredAccountId ? `&accountId=${this.preferredAccountId}` : '');
				} else {
					this.authService.regenerateToken(userType)
						.pipe(take(1))
						.subscribe((isTokenGenerated) => {
							if (!!isTokenGenerated) { location.href = `${location.origin}/accounts?page=` + this.page + (this.preferredAccountId ? `&accountId=${this.preferredAccountId}` : ''); }
						});

					localStorage.removeItem('platform_type_id');
				}
			} else {
				sessionStorage.setItem('initial_email', this.emailFormGroup.get('email').value);
				localStorage.setItem('rurl', `${location.origin}/accounts?isAutoredirect=${this.isAutoredirect ? '1' : ''}&page=` + this.page + (this.preferredAccountId ? `&accountId=${this.preferredAccountId}` : ''));
				this.authService.login();
			}
		});
	}
}
